<template>
  <DialogModal
    :class="[
      'change-log',
      !isLoading && changelog.length && 'change-log--results',
    ]"
    @close-dialog-modal="$emit('close-dialog-modal')"
  >
    <div class="change-log__container">
      <div class="change-log__header">
        <h4 class="modal__title">
          {{ playlistName }}
        </h4>
        <h5 class="modal__sub-title">Change log</h5>
        <p class="modal__info">
          As your skills develop, your playlist content will adapt to match.
          This ensures that what you're learning always aligns with your current
          skill level, providing an optimal training experience.
        </p>
      </div>
      <section v-if="!isLoading" class="change-log__body flat-scroll">
        <div
          v-for="(log, index) in changelog"
          :key="`log-${index}`"
          class="change-log__log-section-container"
        >
          <div class="change-log__title-wrapper">
            <h5 class="modal__section-title">{{ log.reason }}</h5>
            <span class="change-log__date">{{ lastChangeDate(log.date) }}</span>
          </div>
          <div class="change-log__items-container">
            <section v-if="log.added.length" class="change-log__update-section">
              <h5 class="change-log__items-title">
                {{ log.new_difficulty }} training items added
              </h5>
              <ul class="change-log__items-content-wrapper">
                <li
                  v-for="(item, i) in log.added"
                  :key="`item-${i}`"
                  class="change-log__item-wrapper"
                >
                  <span class="change-log__item-type">{{
                    formatContentType(item.content_type)
                  }}</span>
                  <div class="change-log__item-name">{{ item.name }}</div>
                  <ExternalLinkIcon
                    class="change-log__item-cta"
                    @click="goToContent(item)"
                  ></ExternalLinkIcon>
                </li>
              </ul>
            </section>
            <section
              v-if="log.removed.length"
              class="change-log__update-section"
            >
              <h5 class="change-log__items-title">
                {{ log.old_difficulty }} training items removed
              </h5>
              <ul class="change-log__items-content-wrapper">
                <li
                  v-for="(item, i) in log.removed"
                  :key="`item-${i}`"
                  class="change-log__item-wrapper"
                >
                  <span class="change-log__item-type">{{
                    formatContentType(item.content_type)
                  }}</span>
                  <div class="change-log__item-name">{{ item.name }}</div>
                  <ExternalLinkIcon
                    class="change-log__item-cta"
                    @click="goToContent(item)"
                  ></ExternalLinkIcon>
                </li>
              </ul>
            </section>
          </div>
        </div>
        <div v-if="!changelog.length" class="change-log__body--empty">
          No changes logged yet
        </div>
      </section>
      <ActionLoader v-else class="change-log__body change-log__body--loading" />
      <div class="change-log__footer">
        <button
          class="btn btn--primary btn--small"
          @click="$emit('close-dialog-modal')"
        >
          Close
        </button>
      </div>
    </div>
  </DialogModal>
</template>

<script>
import DialogModal from '@/components/modals/DialogModal.vue'
import { recommendedContent } from '@/sonar/helpers/mixins'
import { ExternalLinkIcon } from 'vue-feather-icons'
import { formatDistanceToNow } from 'date-fns'
import { mapActions } from 'vuex'
import ActionLoader from '@/components/shared/ActionLoader.vue'
import CONSTANTS from '@/constants'
export default {
  name: 'PlaylistChangeLog',
  components: {
    DialogModal,
    ExternalLinkIcon,
    ActionLoader,
  },
  mixins: [recommendedContent],
  props: {
    playlistName: {
      type: String,
      required: true,
    },
    playlistId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      isLoading: false,
      changelog: [],
      contentData: [],
    }
  },
  computed: {
    contentIds() {
      const ids = this.changelog.map(group => {
        const added = group?.added.map(c => c.uuid)
        const removed = group?.removed.map(c => c.uuid)
        return [...added, ...removed]
      })
      return [...new Set(ids.flat())]
    },
  },
  async created() {
    this.isLoading = true
    this.changelog = await this.getPlaylistChangeLog(this.playlistId)
    await this.getAlgoliaItems()
    this.processData()
    this.isLoading = false
  },
  methods: {
    ...mapActions({
      getPlaylistChangeLog: 'playlists/getPlaylistChangeLog',
    }),
    async getAlgoliaItems() {
      this.currentItems = await this.getAlgoliaObjects(this.contentIds)
    },
    processData() {
      this.changelog.forEach(sa => {
        const skillArea = sa
        skillArea.added = this.replaceContent(sa.added)
        skillArea.removed = this.replaceContent(sa.removed)
        this.contentData.push(skillArea)
      })
    },
    replaceContent(content) {
      return content?.map(c => {
        const content = this.currentItems.find(
          aItem => aItem.objectID === c.uuid
        )
        return content || c
      })
    },
    formatContentType(contentType) {
      return contentType.replace('-', ' ')
    },
    goToContent(item) {
      const { COURSE, VIDEO } = CONSTANTS.CONTENT_TYPES
      const { content_type, objectID: id } = item
      if (COURSE === content_type) {
        const link = this.$buildContentLink(content_type, {
          ...item,
          id,
        })
        this.$router.push(link)
      } else if (VIDEO === content_type) {
        this.$router.push({
          name: 'search',
          query: { search: item.name, 'content type': VIDEO },
        })
      }
    },
    lastChangeDate(date) {
      return `${formatDistanceToNow(new Date(date), { addSuffix: true })}`
    },
  },
}
</script>

<style lang="scss" scoped>
.change-log {
  ::v-deep .dialog-modal__wrapper {
    max-width: 627px;
    height: 385px;
    padding: 54px 20px 30px 30px;
    top: 5%;
    @include mobile {
      top: 20%;
    }
  }
  &--results {
    ::v-deep .dialog-modal__wrapper {
      height: 90vh;
      @include mobile {
        height: 70vh;
      }
    }
  }
  &__container {
    display: flex;
    flex-direction: column;
    height: 100%;
  }
  &__header {
    margin-right: 10px;
    h4,
    h5 {
      margin: 0;
    }
    p {
      margin: 20px 0;
      line-height: 20px;
    }
  }
  &__date {
    font-size: rem(14);
    color: var(--perfect-gray);
    line-height: rem(20);
    min-width: fit-content;
    margin-left: auto;
  }
  &__body {
    margin-top: 20px;
    padding: 0 10px 0 0;
    display: flex;
    flex-direction: column;
    gap: 20px;
    height: 75%;
    &--loading,
    &--empty {
      display: flex;
      justify-content: center;
      align-items: center;
    }
    &--empty {
      margin: 15px 0 20px 0;
    }
  }
  &__log-section-container {
    display: flex;
    flex-direction: column;
    border-bottom: 1px solid var(--tundora-gray);
    padding-bottom: 20px;
    &:last-child {
      border-bottom: none;
    }
  }
  &__title-wrapper {
    display: flex;
    gap: 5px;
    margin-bottom: 15px;
  }
  &__update-section {
    font-size: rem(14);
  }
  &__items {
    &-container {
      border-left: 4px solid var(--dove-gray);
      padding-left: 15px;
      display: flex;
      flex-direction: column;
      gap: 10px;
    }
    &-title {
      font-size: rem(14);
      line-height: rem(18);
      margin: 0;
      color: var(--dusty-gray);
      margin-bottom: 5px;
      &:first-letter {
        text-transform: uppercase;
      }
    }
    &-content-wrapper {
      margin: 0;
      padding: 0;
      li {
        list-style-type: none;
      }
    }
  }
  &__item {
    &-wrapper {
      display: flex;
      gap: 5px;
      margin-bottom: 3px;
      &:last-child {
        margin-bottom: 0;
      }
    }
    &-type {
      text-transform: capitalize;
      min-width: fit-content;
    }
    &-name {
      color: var(--perfect-gray);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    &-cta {
      color: var(--purple-pale);
      min-width: 16px;
      width: 16px;
      height: 16px;
      margin-left: auto;
      cursor: pointer;
    }
  }
  &__footer {
    display: flex;
    justify-content: flex-end;
    margin: 30px 10px 0 0;
  }
}
</style>
